import { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, Info, Loader2, Upload, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/status-badge';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DatePicker, civilFromInstant, instantFromCivil } from '@/components/ui/date-picker';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { defaultCardReconciliationRange } from '@/lib/reconciliation-default-range';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';
import { toast } from '@/stores/toast-store';
import {
  useCcFundingMatched,
  useCcFundingUnmatchedSource,
  useCcFundingUnmatchedFunding,
  useCcFundingSourceRows,
  useCcFundingTargetRows,
  useCcFundingDuplicateSource,
  useCcFundingVoidedSource,
  useCcFundingVoidedRecapturedSource,
  useCcFundingSummary,
  useUploadCcFundingWorkbook,
  type CcFundingMatchedRow,
  type CcFundingUnmatchedSourceRow,
  type CcFundingUnmatchedFundingRow,
  type CcFundingSourceRow,
  type CcFundingTargetRow,
  type CcFundingDuplicateSourceRow,
  type CcFundingVoidedSourceRow,
  type CcFundingPagination,
} from '@/hooks/use-cc-funding-reconciliation';

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Bridge the string 'YYYY-MM-DD' filter state to the DatePicker's Date value.
// Both directions resolve the day in the *reporting* zone — the same zone the
// DatePicker renders in — so the displayed date matches the picked day
// regardless of the browser timezone. Building the Date in browser-local time
// (the old behaviour) shifted the display back a day whenever the browser zone
// was ahead of the reporting zone (e.g. IST vs Pacific), while the query — which
// uses the tz-neutral 'YYYY-MM-DD' string — stayed correct.
const ymdToDate = (s: string, tz: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return y && m && d ? instantFromCivil({ year: y, month: m, day: d, hour: 0, minute: 0, second: 0 }, tz) : null;
};
const dateToYmd = (d: Date | null, tz: string): string => {
  if (!d) return '';
  const c = civilFromInstant(d, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}`;
};

// "Local Sale Date" / "Funding Date" are stored as the literal wall-clock
// digits from the uploaded spreadsheet (no real timezone attached), saved
// as if they were UTC just to preserve the exact numbers. The shared
// formatDateTime() renders in the *browser's* local timezone, which shifts
// these away from the original sheet value for any viewer not in UTC (e.g.
// IST +5:30 pushes anything after ~6:30 PM into the next calendar day).
// Reading the UTC components back directly reproduces the source value.
function formatSourceDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

type CcFundingTab =
  | 'unmatched-source'
  | 'unmatched-funding'
  | 'duplicate-transactions'
  | 'source-rows'
  | 'voided'
  | 'voided-recaptured'
  | 'funding-rows'
  | 'matched';

const CC_FUNDING_TABS: { id: CcFundingTab; label: string; help?: string }[] = [
  {
    id: 'unmatched-source',
    label: 'Unmatched in POS',
    help: 'Transactions not found in the POS; no Order ID is available.',
  },
  {
    id: 'unmatched-funding',
    label: 'Unmatched Backend - Funding',
    help: 'Transactions processed through the POS but not found in funding records.',
  },
  {
    id: 'duplicate-transactions',
    label: 'Duplicate - In POS',
    help: 'Transactions with the same card and amount processed within one minute.',
  },
  { id: 'source-rows', label: 'POS Orders by Cards', help: 'POS transactions with associated card details.' },
  {
    id: 'voided',
    label: 'Voided',
    help: 'Transactions reversed before batch settlement and not processed again.',
  },
  {
    id: 'voided-recaptured',
    label: 'Voided But Recaptured',
    help: 'Transactions reversed before batch settlement and subsequently processed again.',
  },
  {
    id: 'funding-rows',
    label: 'Funded Transactions',
    help: 'Transactions successfully processed and sent to the bank.',
  },
  {
    id: 'matched',
    label: 'Matched POS to Funding',
    help: 'Transactions successfully matched between POS and funding records.',
  },
];

function CcFundingSummaryStat({
  label,
  count,
  total,
  accent,
}: {
  label: string;
  count?: number;
  total?: string;
  accent?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-xl font-semibold', accent && 'text-amber-600')}>{count ?? '—'}</p>
      <p className="text-sm text-muted-foreground">{total != null ? formatCurrency(total) : ''}</p>
    </div>
  );
}

function CcFundingTable({
  tab,
  rows,
  isLoading,
  sortBy,
  sortDir,
  onSortChange,
  filters,
  onFilterChange,
}: {
  tab: CcFundingTab;
  rows: unknown[];
  isLoading: boolean;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSortChange: (field: string) => void;
  filters: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
}) {
  const columns = CC_FUNDING_SORT_OPTIONS[tab];
  const colCount = columns.length;

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
          {columns.map((col) => {
            const isSorted = sortBy === col.value;
            return (
              <th key={col.value} className="px-4 py-2 font-medium">
                <button
                  type="button"
                  onClick={() => onSortChange(col.value)}
                  className="flex items-center gap-1 whitespace-nowrap hover:text-foreground"
                >
                  {col.label}
                  {isSorted ? (
                    <ArrowUpDown className={cn('h-3 w-3 text-primary', sortDir === 'asc' && 'rotate-180')} />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-muted-foreground/40" />
                  )}
                </button>
              </th>
            );
          })}
        </tr>
        <tr className="border-b border-border bg-white">
          {columns.map((col) => (
            <th key={col.value} className="px-4 py-1.5 font-normal">
              <input
                type="text"
                value={filters[col.value] ?? ''}
                onChange={(e) => onFilterChange(col.value, e.target.value)}
                placeholder="Filter…"
                className="w-full min-w-[90px] rounded border border-input bg-white px-2 py-1 text-xs normal-case text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={colCount} className="px-4 py-10 text-center text-muted-foreground">
              Loading…
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={colCount}>
              <EmptyState icon={SearchX} title="No rows found" description="Try adjusting the filters above." />
            </td>
          </tr>
        ) : (
          rows.map((row) => <CcFundingTableRow key={JSON.stringify(row)} tab={tab} row={row} />)
        )}
      </tbody>
    </table>
  );
}

function CcFundingTableRow({ tab, row }: { tab: CcFundingTab; row: unknown }) {
  switch (tab) {
    case 'matched': {
      const r = row as CcFundingMatchedRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.localSaleDate)}</td>
          <td className="px-4 py-3">{formatSourceDateTime(r.fundingDate)}</td>
          <td className="px-4 py-3">{r.lagDays}</td>
          <td className="px-4 py-3 font-mono">{r.approvalCode}</td>
          <td className="px-4 py-3">**** {r.cardLastFour}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.approvedAmount)}</td>
          <td className="px-4 py-3">{r.invoiceNumber ?? ''}</td>
          <td className="px-4 py-3">{r.orderId ?? ''}</td>
        </tr>
      );
    }
    case 'unmatched-source': {
      const r = row as CcFundingUnmatchedSourceRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.localSaleDate)}</td>
          <td className="px-4 py-3 font-mono">{r.approvalCode}</td>
          <td className="px-4 py-3">**** {r.cardLastFour}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.approvedAmount)}</td>
          <td className="px-4 py-3">{r.invoiceNumber ?? ''}</td>
          <td className="px-4 py-3">{r.customerName ?? ''}</td>
        </tr>
      );
    }
    case 'unmatched-funding': {
      const r = row as CcFundingUnmatchedFundingRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.fundingDate)}</td>
          <td className="px-4 py-3 font-mono">{r.authCode}</td>
          <td className="px-4 py-3">**** {r.lastFour}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.amount)}</td>
          <td className="px-4 py-3">{r.transactionNumber}</td>
          <td className="px-4 py-3">{r.orderId ?? ''}</td>
        </tr>
      );
    }
    case 'source-rows': {
      const r = row as CcFundingSourceRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.localSaleDate)}</td>
          <td className="px-4 py-3">{r.storeAlias ?? r.store ?? ''}</td>
          <td className="px-4 py-3 font-mono">{r.approvalCode}</td>
          <td className="px-4 py-3">**** {r.cardLastFour}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.approvedAmount)}</td>
          <td className="px-4 py-3">{r.invoiceNumber ?? ''}</td>
          <td className="px-4 py-3">{r.customerName ?? ''}</td>
          <td className="px-4 py-3">{r.batchStatus ?? ''}</td>
        </tr>
      );
    }
    case 'funding-rows': {
      const r = row as CcFundingTargetRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.fundingDate)}</td>
          <td className="px-4 py-3">{r.transactionNumber}</td>
          <td className="px-4 py-3 font-mono">{r.authCode}</td>
          <td className="px-4 py-3">**** {r.lastFour}</td>
          <td className="px-4 py-3">{r.brand ?? ''}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.amount)}</td>
          <td className="px-4 py-3">{r.status && <StatusBadge status={r.status} />}</td>
          <td className="px-4 py-3">{r.orderId ?? ''}</td>
        </tr>
      );
    }
    case 'duplicate-transactions': {
      const r = row as CcFundingDuplicateSourceRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.localSaleDate)}</td>
          <td className="px-4 py-3 font-mono">{r.approvalCode}</td>
          <td className="px-4 py-3">**** {r.cardLastFour}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.approvedAmount)}</td>
          <td className="px-4 py-3">{r.invoiceNumber ?? ''}</td>
          <td className="px-4 py-3">{r.customerName ?? ''}</td>
          <td className="px-4 py-3 text-right font-semibold text-amber-600">{r.duplicateCount}×</td>
        </tr>
      );
    }
    case 'voided':
    case 'voided-recaptured': {
      const r = row as CcFundingVoidedSourceRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatSourceDateTime(r.localSaleDate)}</td>
          <td className="px-4 py-3 font-mono">{r.approvalCode}</td>
          <td className="px-4 py-3">**** {r.cardLastFour}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.approvedAmount)}</td>
          <td className="px-4 py-3">{r.invoiceNumber ?? ''}</td>
          <td className="px-4 py-3">{r.customerName ?? ''}</td>
          <td className="px-4 py-3">{r.transType ?? ''}</td>
        </tr>
      );
    }
    default:
      return null;
  }
}

const CC_FUNDING_SORT_OPTIONS: Record<CcFundingTab, { value: string; label: string }[]> = {
  matched: [
    { value: 'localSaleDate', label: 'Local Sale Date' },
    { value: 'fundingDate', label: 'Funded Date' },
    { value: 'lagDays', label: 'Lag (days)' },
    { value: 'approvalCode', label: 'Approval/Auth Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'approvedAmount', label: 'Amount' },
    { value: 'invoiceNumber', label: 'Invoice #' },
    { value: 'orderId', label: 'Order ID' },
  ],
  'unmatched-source': [
    { value: 'localSaleDate', label: 'Local Sale Date' },
    { value: 'approvalCode', label: 'Approval Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'approvedAmount', label: 'Amount' },
    { value: 'invoiceNumber', label: 'Invoice #' },
    { value: 'customerName', label: 'Customer' },
  ],
  'unmatched-funding': [
    { value: 'fundingDate', label: 'Funded Date' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'lastFour', label: 'Card' },
    { value: 'amount', label: 'Amount' },
    { value: 'transactionNumber', label: 'Transaction #' },
    { value: 'orderId', label: 'Order ID' },
  ],
  'source-rows': [
    { value: 'localSaleDate', label: 'Local Sale Date' },
    { value: 'store', label: 'Store' },
    { value: 'approvalCode', label: 'Approval Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'approvedAmount', label: 'Amount' },
    { value: 'invoiceNumber', label: 'Invoice #' },
    { value: 'customerName', label: 'Customer' },
    { value: 'batchStatus', label: 'Batch Status' },
  ],
  'funding-rows': [
    { value: 'fundingDate', label: 'Funded Date' },
    { value: 'transactionNumber', label: 'Transaction #' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'lastFour', label: 'Card' },
    { value: 'brand', label: 'Brand' },
    { value: 'amount', label: 'Amount' },
    { value: 'status', label: 'Status' },
    { value: 'orderId', label: 'Order ID' },
  ],
  'duplicate-transactions': [
    { value: 'localSaleDate', label: 'Txn Date' },
    { value: 'approvalCode', label: 'Auth Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'approvedAmount', label: 'Amount' },
    { value: 'invoiceNumber', label: 'Invoice #' },
    { value: 'customerName', label: 'Customer' },
    { value: 'duplicateCount', label: 'Occurrences' },
  ],
  voided: [
    { value: 'localSaleDate', label: 'Local Sale Date' },
    { value: 'approvalCode', label: 'Approval Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'approvedAmount', label: 'Amount' },
    { value: 'invoiceNumber', label: 'Invoice #' },
    { value: 'customerName', label: 'Customer' },
    { value: 'transType', label: 'Trans Type' },
  ],
  'voided-recaptured': [
    { value: 'localSaleDate', label: 'Local Sale Date' },
    { value: 'approvalCode', label: 'Approval Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'approvedAmount', label: 'Amount' },
    { value: 'invoiceNumber', label: 'Invoice #' },
    { value: 'customerName', label: 'Customer' },
    { value: 'transType', label: 'Trans Type' },
  ],
};

export function RevelReconciliationPanel() {
  const { selectedLocationId } = useLocationStore();
  // The zone the DatePicker renders in; the date bridge must use the same one.
  const { timeZone: reportTz } = useReportingTimeZone();
  // Uploading a workbook writes bulk financial data, so it has its own key.
  const canUpload = usePermissions().has('reconciliation.upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadCcFundingWorkbook();

  // Card reconciliation opens on a default range (1st of the month → today − 4
  // days) so records are on screen without the user touching the pickers. Both
  // the inputs and `applied` are seeded, so the first query already carries the
  // dates — no Apply click needed. Resolved once per mount via the initializer.
  const [defaultRange] = useState(() => defaultCardReconciliationRange(reportTz));

  const [activeTab, setActiveTab] = useState<CcFundingTab>('unmatched-source');
  const [dateFromInput, setDateFromInput] = useState(defaultRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(defaultRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>(defaultRange);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortBy, setSortBy] = useState(CC_FUNDING_SORT_OPTIONS['unmatched-source'][0].value);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});

  // Debounce the per-column filter row so typing doesn't refetch on every
  // keystroke, matching the search-debounce pattern used elsewhere in the app.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilters(columnFilters);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [columnFilters]);

  const baseParams = {
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
  };
  const filtersParam = Object.keys(appliedFilters).length > 0 ? JSON.stringify(appliedFilters) : undefined;
  const listParams = { ...baseParams, page, perPage, sortBy, sortDir, filters: filtersParam };

  const { data: summaryData } = useCcFundingSummary(baseParams);
  const summary = summaryData?.data;

  const matchedQuery = useCcFundingMatched(listParams, activeTab === 'matched');
  const unmatchedSourceQuery = useCcFundingUnmatchedSource(listParams, activeTab === 'unmatched-source');
  const unmatchedFundingQuery = useCcFundingUnmatchedFunding(listParams, activeTab === 'unmatched-funding');
  const sourceRowsQuery = useCcFundingSourceRows(listParams, activeTab === 'source-rows');
  const fundingRowsQuery = useCcFundingTargetRows(listParams, activeTab === 'funding-rows');
  const duplicateSourceQuery = useCcFundingDuplicateSource(listParams, activeTab === 'duplicate-transactions');
  const voidedQuery = useCcFundingVoidedSource(listParams, activeTab === 'voided');
  const voidedRecapturedQuery = useCcFundingVoidedRecapturedSource(listParams, activeTab === 'voided-recaptured');

  interface ActiveQueryShape {
    data?: { data: unknown[]; pagination: CcFundingPagination };
    isLoading: boolean;
  }
  const queriesByTab: Record<CcFundingTab, ActiveQueryShape> = {
    matched: matchedQuery,
    'unmatched-source': unmatchedSourceQuery,
    'unmatched-funding': unmatchedFundingQuery,
    'source-rows': sourceRowsQuery,
    'funding-rows': fundingRowsQuery,
    'duplicate-transactions': duplicateSourceQuery,
    voided: voidedQuery,
    'voided-recaptured': voidedRecapturedQuery,
  };
  const activeQuery = queriesByTab[activeTab];
  const rows = activeQuery.data?.data ?? [];
  const pagination = activeQuery.data?.pagination;
  const activeTabDef = CC_FUNDING_TABS.find((t) => t.id === activeTab);

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    // Back to the page's opening state, i.e. the default range — clearing to
    // blank would leave no way to get the default back without a reload.
    setDateFromInput(defaultRange.dateFrom);
    setDateToInput(defaultRange.dateTo);
    setApplied(defaultRange);
    setColumnFilters({});
    setAppliedFilters({});
    setSortBy(CC_FUNDING_SORT_OPTIONS[activeTab][0].value);
    setSortDir('desc');
    setPage(1);
  };
  const handleTabChange = (tab: CcFundingTab) => {
    setActiveTab(tab);
    setPage(1);
    setSortBy(CC_FUNDING_SORT_OPTIONS[tab][0].value);
    setSortDir('desc');
    setColumnFilters({});
    setAppliedFilters({});
  };
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };
  const handleFilterChange = (field: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedLocationId) {
      toast.error('Select a location before uploading');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      const result = await uploadMutation.mutateAsync({ locationId: selectedLocationId, file });
      if (result.duplicate) {
        toast.info('This exact file was already uploaded — no new rows added.');
      } else {
        const skippedNote = result.rowErrors.length > 0 ? `, ${result.rowErrors.length} row(s) skipped` : '';
        toast.success(
          `Uploaded ${result.fileName}: ${result.sourceRowsInserted} source rows, ${result.fundingRowsInserted} funding rows${skippedNote}`,
        );
      }
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message ?? 'Upload failed';
      toast.error(message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {canUpload && (
        <div className="flex justify-end">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
          <Button
            variant="primary"
            size="sm"
            disabled={uploadMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {uploadMutation.isPending ? 'Uploading…' : 'Upload Workbook'}
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <CcFundingSummaryStat
            label="Unmatched in Source"
            count={summary?.unmatchedSource.count}
            total={summary?.unmatchedSource.total}
          />
          <CcFundingSummaryStat
            label="Unmatched in Funded"
            count={summary?.unmatchedFunding.count}
            total={summary?.unmatchedFunding.total}
          />
          <CcFundingSummaryStat
            label="Duplicates"
            count={summary?.duplicates.count}
            total={summary?.duplicates.total}
            accent
          />
          <CcFundingSummaryStat label="Matched" count={summary?.matched.count} total={summary?.matched.total} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <DatePicker
          mode="date"
          timezone={reportTz}
          className="w-44"
          placeholder="Start date"
          value={ymdToDate(dateFromInput, reportTz)}
          onChange={(v) => setDateFromInput(dateToYmd(v as Date | null, reportTz))}
          maxDate={ymdToDate(dateToInput, reportTz) ?? undefined}
        />
        <DatePicker
          mode="date"
          timezone={reportTz}
          className="w-44"
          placeholder="End date"
          value={ymdToDate(dateToInput, reportTz)}
          onChange={(v) => setDateToInput(dateToYmd(v as Date | null, reportTz))}
          minDate={ymdToDate(dateFromInput, reportTz) ?? undefined}
        />
        <Button size="sm" variant="primary" onClick={handleApply}>
          Apply
        </Button>
        <Button size="sm" variant="destructive-outline" onClick={handleReset}>
          Reset
        </Button>
        <SearchableSelect
          className="w-72"
          clearable={false}
          value={activeTab}
          onChange={(v) => handleTabChange(v as CcFundingTab)}
          options={CC_FUNDING_TABS.map((t) => ({ value: t.id, label: t.label }))}
          placeholder="Select a view"
          searchPlaceholder="Find a view..."
        />
      </div>

      {activeTabDef?.help && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>
            <span className="font-medium text-foreground">{activeTabDef.label}:</span> {activeTabDef.help}
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <CcFundingTable
            tab={activeTab}
            rows={rows}
            isLoading={activeQuery.isLoading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            filters={columnFilters}
            onFilterChange={handleFilterChange}
          />
        </div>
        {pagination && pagination.total > 0 && (
          <DataTablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            perPage={pagination.perPage}
            onPageChange={setPage}
            perPageOptions={[10, 25, 50, 100]}
            onPerPageChange={(val) => {
              setPerPage(val);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
